<script lang="ts" setup>
import { Icon } from '~ds/components/ui/icon';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '~ds/components/ui/pagination';
import { ICONS } from '~ds/lib/icons';
import { translateWithFallback } from '~kui/utils/i18n';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  /** Total number of items across all pages (not the page count). */
  total: number;
  /** Items shown per page — with `total`, determines how many numbered pages render. */
  itemsPerPage: number;
  /** Pages kept either side of the current page before collapsing into an ellipsis. */
  siblingCount?: number;
}

withDefaults(defineProps<Props>(), {
  siblingCount: 1,
});

const page = defineModel<number>('page', { required: true });

const { t } = useI18n();

const previousText = computed(() => translateWithFallback(t, 'general.pagination.previousText', 'Prev'));
const previousAriaLabel = computed(() => translateWithFallback(t, 'general.pagination.previousAriaLabel', 'Previous page'));
const nextText = computed(() => translateWithFallback(t, 'general.pagination.nextText', 'Next'));
const nextAriaLabel = computed(() => translateWithFallback(t, 'general.pagination.nextAriaLabel', 'Next page'));
const morePagesText = computed(() => translateWithFallback(t, 'general.pagination.morePagesText', 'More pages'));

const itemAriaLabel = (value: number) =>
  translateWithFallback(t, 'general.pagination.itemAriaLabel', 'Page-:item', { item: value });
</script>

<template>
  <Pagination
    v-model:page="page"
    :total="total"
    :items-per-page="itemsPerPage"
    :sibling-count="siblingCount"
    show-edges
  >
    <PaginationContent v-slot="{ items }">
      <PaginationPrevious :aria-label="previousAriaLabel">
        <Icon :name="ICONS.arrowLeft" size="sm" />
        <span class="hidden sm:block">{{ previousText }}</span>
      </PaginationPrevious>
      <template v-for="(item, index) in items" :key="index">
        <PaginationItem
          v-if="item.type === 'page'"
          :value="item.value"
          :is-active="item.value === page"
          :aria-label="itemAriaLabel(item.value)"
        >
          {{ item.value }}
        </PaginationItem>
        <PaginationEllipsis v-else :index="index">
          <Icon :name="ICONS.moreHorizontal" size="sm" />
          <span class="sr-only">{{ morePagesText }}</span>
        </PaginationEllipsis>
      </template>
      <PaginationNext :aria-label="nextAriaLabel">
        <span class="hidden sm:block">{{ nextText }}</span>
        <Icon :name="ICONS.arrowRight" size="sm" />
      </PaginationNext>
    </PaginationContent>
  </Pagination>
</template>
